import { useContext, useEffect, useMemo, useState } from 'react';

import { FeatureFlagsContext, FeatureFlagsContextValue } from '../../../context/FeatureFlags';
import { parseFeatureFlagValue } from '../../../context/FeatureFlags/parseFeatureFlagValue';
import {
  LaunchDarklyContext,
  LaunchDarklyContextManager as BrowserLaunchDarklyContextManager,
} from '../../../context/LaunchDarkly';
import { isFeatureFlagsViaGql } from '../../../utils/featureFlags/isFeatureFlagsViaGql';

/** @deprecated Use FeatureFlagsContext via useFeatureFlagsContext for new code. */
export type LaunchDarklyContextManager = Pick<FeatureFlagsContextValue, 'isReady'> & {
  client: undefined;
  currentContext: undefined;
};

const useLaunchDarklyFromGql = (): LaunchDarklyContextManager => {
  const { isReady } = useContext(FeatureFlagsContext);

  return useMemo(
    () => ({
      client: undefined,
      currentContext: undefined,
      isReady,
    }),
    [isReady]
  );
};

const useLaunchDarklyFromBrowser = (): BrowserLaunchDarklyContextManager => {
  return useContext(LaunchDarklyContext);
};

export const useLaunchDarkly = (): LaunchDarklyContextManager | BrowserLaunchDarklyContextManager => {
  const gqlResult = useLaunchDarklyFromGql();
  const browserResult = useLaunchDarklyFromBrowser();

  return isFeatureFlagsViaGql() ? gqlResult : browserResult;
};

const useLaunchDarklyFlagFromGql = <T>(flagKey: string, defaultValue: T): { value: T; loading: boolean } => {
  const { flags, isReady, loading } = useContext(FeatureFlagsContext);

  const value = useMemo(() => {
    if (!(flagKey in flags)) {
      return defaultValue;
    }

    const rawValue = flags[flagKey];

    if (rawValue === null || rawValue === undefined) {
      return defaultValue;
    }

    if (typeof rawValue === typeof defaultValue) {
      return rawValue as T;
    }

    if (typeof rawValue === 'string') {
      return parseFeatureFlagValue(rawValue, defaultValue);
    }

    return rawValue as T;
  }, [defaultValue, flagKey, flags]);

  return {
    value,
    loading: loading || !isReady,
  };
};

const useLaunchDarklyFlagFromBrowser = <T>(flagKey: string, defaultValue: T): { value: T; loading: boolean } => {
  const { client, currentContext, isReady } = useLaunchDarklyFromBrowser();
  const [value, setValue] = useState<T>(defaultValue);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isReady || !client) {
      setLoading(true);
      return;
    }

    const updateFlag = (): void => {
      const flagValue = client.variation(flagKey, defaultValue);
      setValue(flagValue);
      setLoading(false);
    };

    updateFlag();

    const handleChange = (): void => updateFlag();
    client.on(`change:${flagKey}`, handleChange);
    client.on('change', handleChange);

    return () => {
      client.off(`change:${flagKey}`, handleChange);
      client.off('change', handleChange);
    };
  }, [client, currentContext, flagKey, defaultValue, isReady]);

  return { value, loading };
};

export const useLaunchDarklyFlag = <T>(flagKey: string, defaultValue: T): { value: T; loading: boolean } => {
  const gqlResult = useLaunchDarklyFlagFromGql(flagKey, defaultValue);
  const browserResult = useLaunchDarklyFlagFromBrowser(flagKey, defaultValue);

  return isFeatureFlagsViaGql() ? gqlResult : browserResult;
};

export default useLaunchDarklyFlag;
